"use client";

import { type RefObject, useEffect, useState } from "react";

const OBSTACLES = '[data-chicky-obstacle], [role="dialog"], [role="menu"]';
const SURFACES = "[data-chicky-surface]";

/** Keep decorative Chicky out of foreground surfaces, including portalled dialogs. */
export function useDeskTutorClearance(
  sceneRef: RefObject<HTMLDivElement | null>,
  characterRef: RefObject<HTMLButtonElement | null>,
  replyRef: RefObject<HTMLSpanElement | null>
) {
  const [presentation, setPresentation] = useState({ isClear: false, isBehindContent: false });

  useEffect(() => {
    const scene = sceneRef.current;
    const character = characterRef.current;
    if (!scene || !character) return;

    let clear = false;
    let frame: number | null = null;
    const observed = new Set<Element>();
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => schedule());

    const measure = () => {
      frame = null;
      const sceneRect = scene.getBoundingClientRect();
      const width = Number.parseFloat(character.style.width);
      // Use the desk anchor rather than the fading/pressed button's transformed bounds.
      const x = sceneRect.left + Number.parseFloat(character.style.left);
      const y = sceneRect.top + Number.parseFloat(character.style.top);
      if (!sceneRect.width || !sceneRect.height || !width || !Number.isFinite(x + y)) {
        clear = false;
        setPresentation((previous) =>
          previous.isClear ? { ...previous, isClear: false } : previous
        );
        return;
      }

      // Union of the six frames' opaque pixels after the sprite's horizontal flip.
      // Transparent padding is almost half the frame and must not trigger a fade.
      const footprints = [
        {
          left: x - width / 2 + width * (46 / 192),
          right: x - width / 2 + width * (147 / 192),
          top: y - width * (203 / 192),
          bottom: y - width * (5 / 192),
        },
      ];
      const reply = replyRef.current;
      if (reply) footprints.push(reply.getBoundingClientRect());
      const obstacles = Array.from(document.querySelectorAll<HTMLElement>(OBSTACLES));
      const surfaces = Array.from(document.querySelectorAll<HTMLElement>(SURFACES));
      const nextObserved = new Set<Element>([
        scene,
        ...obstacles,
        ...surfaces,
        ...(reply ? [reply] : []),
      ]);
      observed.forEach((element) => {
        if (!nextObserved.has(element)) {
          resizeObserver?.unobserve(element);
          observed.delete(element);
        }
      });
      nextObserved.forEach((element) => {
        if (!observed.has(element)) {
          resizeObserver?.observe(element);
          observed.add(element);
        }
      });

      const clearance = clear ? 8 : 16;
      const intersects = (element: HTMLElement, margin: number) => {
        const rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height || getComputedStyle(element).visibility === "hidden") {
          return false;
        }
        return footprints.some(
          (footprint) =>
            footprint.left < rect.right + margin &&
            footprint.right > rect.left - margin &&
            footprint.top < rect.bottom + margin &&
            footprint.bottom > rect.top - margin
        );
      };
      clear = !obstacles.some((element) => intersects(element, clearance));
      // Translucent panel chrome may cover Chicky without hiding it altogether.
      const isBehindContent = surfaces.some((element) => intersects(element, 0));
      if ((!clear || isBehindContent) && document.activeElement === character) character.blur();
      setPresentation((previous) =>
        previous.isClear === clear && previous.isBehindContent === isBehindContent
          ? previous
          : { isClear: clear, isBehindContent }
      );
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure);
    };
    // Observe layout changes and mounted menus without polling. Ignore our own fade styles.
    const mutations = new MutationObserver((records) => {
      if (records.some((record) => record.type === "childList" || !scene.contains(record.target)))
        schedule();
    });
    mutations.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        "class",
        "style",
        "hidden",
        "data-state",
        "data-chicky-obstacle",
        "data-chicky-surface",
      ],
    });
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    measure();

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      mutations.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
    };
  }, [sceneRef, characterRef, replyRef]);

  return presentation;
}
